/**
 * Sync nuvem leve para o link público (?agendar=1) e o app Admin.
 * Usa /api/estado na mesma origem (Cloudflare Pages Function) quando disponível.
 * Fallback: só armazenamento local (NW.js / APK).
 */

import type { BarbeariaCliente, BarbeariaEstado, BarbeariaAgendamento } from './barbeariaTipos';

const SYNC_TOKEN_KEY = 'carioca-sync-token-v1';

/** Domínio oficial do link do cliente (Cloudflare Pages). */
export const CARIOCA_SITE_OFICIAL = 'https://carioca-barbershop.pages.dev';

export function apiEstadoUrl(): string {
  try {
    const base = import.meta.env.VITE_CARIOCA_API_URL as string | undefined;
    if (base && String(base).trim()) {
      return String(base).replace(/\/$/, '') + '/api/estado';
    }
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    // NW.js file:// ou Capacitor: usa site oficial
    if (window.location.protocol === 'file:' || window.location.hostname === 'localhost') {
      return `${CARIOCA_SITE_OFICIAL}/api/estado`;
    }
    return `${window.location.origin}/api/estado`;
  }
  return `${CARIOCA_SITE_OFICIAL}/api/estado`;
}

export function obterTokenSync(): string {
  try {
    let t = localStorage.getItem(SYNC_TOKEN_KEY);
    if (!t) {
      t = `bb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SYNC_TOKEN_KEY, t);
    }
    return t;
  } catch {
    return 'anon';
  }
}

export async function puxarEstadoNuvem(): Promise<BarbeariaEstado | null> {
  const url = apiEstadoUrl();
  if (!url) return null;
  try {
    const res = await fetch(url, { method: 'GET', cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || typeof data !== 'object' || !data.estado) return null;
    return data.estado as BarbeariaEstado;
  } catch {
    return null;
  }
}

/**
 * Health-check da API do site (link do cliente).
 * GET /api/estado com 200 = API no ar (mesmo com estado vazio).
 * Rede/5xx = fora — o cliente não deve poder enviar agendamento.
 */
export async function checarApiNuvemDisponivel(): Promise<boolean> {
  const url = apiEstadoUrl();
  if (!url) return false;
  try {
    const res = await fetch(url, { method: 'GET', cache: 'no-store' });
    return res.ok;
  } catch {
    return false;
  }
}

export async function publicarEstadoNuvem(estado: BarbeariaEstado): Promise<boolean> {
  const url = apiEstadoUrl();
  if (!url) return false;
  try {
    const res = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Carioca-Token': obterTokenSync(),
      },
      body: JSON.stringify({ estado, token: obterTokenSync() }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export type ResultadoAgendarNuvem =
  | { ok: true; estado?: BarbeariaEstado }
  | { ok: false; conflito?: boolean; erro?: string; estado?: BarbeariaEstado };

export async function publicarAgendamentoNuvem(params: {
  agendamento: BarbeariaAgendamento;
  cliente: BarbeariaCliente;
  duracaoMin?: number;
}): Promise<ResultadoAgendarNuvem> {
  const url = apiEstadoUrl();
  if (!url) return { ok: false, erro: 'Sem conexão com a nuvem' };
  try {
    const res = await fetch(url.replace(/\/estado$/, '/agendar'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    let data: {
      ok?: boolean;
      erro?: string;
      conflito?: boolean;
      estado?: BarbeariaEstado;
    } = {};
    try {
      data = await res.json();
    } catch {
      /* ignore */
    }
    if (res.status === 409 || data.conflito) {
      return {
        ok: false,
        conflito: true,
        erro: data.erro || 'Este horário não está mais disponível. Escolha outro.',
        estado: data.estado,
      };
    }
    if (!res.ok) {
      return { ok: false, erro: data.erro || `Erro ${res.status}`, estado: data.estado };
    }
    return { ok: true, estado: data.estado };
  } catch {
    return { ok: false, erro: 'Falha de rede ao confirmar' };
  }
}

/** Une mapas de remoção (fica a data mais recente por id). */
function mesclarRemocoes(
  a: Record<string, string> | undefined,
  b: Record<string, string> | undefined
): Record<string, string> {
  const out: Record<string, string> = { ...(a || {}) };
  for (const [id, em] of Object.entries(b || {})) {
    const prev = out[id];
    if (!prev || (Date.parse(em) || 0) >= (Date.parse(prev) || 0)) out[id] = em;
  }
  return out;
}

function filtrarAgendamentosRemovidos(
  agendamentos: BarbeariaAgendamento[],
  remocoes: Record<string, string>
): BarbeariaAgendamento[] {
  return agendamentos.filter((a) => {
    const rem = remocoes[a.id];
    if (!rem) return true;
    // Só “volta” se o agendamento foi recriado/atualizado DEPOIS da remoção
    return (Date.parse(a.atualizadoEm) || 0) > (Date.parse(rem) || 0);
  });
}

/** Une dois estados. Remoções da loja vencem itens antigos da nuvem.
 * Proteções: não apaga agenda local com nuvem vazia; catálogo/estoque/despesas não somem no sync.
 */
export function mesclarEstadosBarbearia(
  local: BarbeariaEstado,
  remoto: BarbeariaEstado | null
): BarbeariaEstado {
  if (!remoto) {
    const remocoes = local.remocoesAgendamento || {};
    return {
      ...local,
      remocoesAgendamento: remocoes,
      agendamentos: filtrarAgendamentosRemovidos(local.agendamentos, remocoes),
    };
  }

  const remocoes = mesclarRemocoes(local.remocoesAgendamento, remoto.remocoesAgendamento);
  const tLocal = Date.parse(local.atualizadoEm) || 0;
  const tRemoto = Date.parse(remoto.atualizadoEm) || 0;

  // Nuvem “vazia” de agenda não apaga a agenda local (ex.: após atualizar o NW/APK)
  const remotoSemAgenda =
    (remoto.agendamentos?.length || 0) === 0 && (local.agendamentos?.length || 0) > 0;

  const preferLocal = remotoSemAgenda || tLocal >= tRemoto;
  const primary = preferLocal ? local : remoto;
  const secondary = preferLocal ? remoto : local;

  const mapAg = new Map<string, BarbeariaAgendamento>();
  for (const a of secondary.agendamentos) mapAg.set(a.id, a);
  for (const a of primary.agendamentos) {
    const prev = mapAg.get(a.id);
    if (!prev || (Date.parse(a.atualizadoEm) || 0) >= (Date.parse(prev.atualizadoEm) || 0)) {
      mapAg.set(a.id, a);
    }
  }

  const mapCli = new Map<string, BarbeariaCliente>();
  for (const c of [...secondary.clientes, ...primary.clientes]) {
    const prev = mapCli.get(c.id);
    if (!prev || (Date.parse(c.atualizadoEm) || 0) >= (Date.parse(prev.atualizadoEm) || 0)) {
      mapCli.set(c.id, c);
    }
  }

  const mapBlk = new Map<string, (typeof primary.bloqueios)[0]>();
  for (const b of [...secondary.bloqueios, ...primary.bloqueios]) mapBlk.set(b.id, b);

  const mapDesp = new Map<string, (typeof local.despesas)[0]>();
  for (const d of [...(remoto.despesas || []), ...(local.despesas || [])]) {
    const prev = mapDesp.get(d.id);
    if (!prev) mapDesp.set(d.id, d);
  }

  const mapEst = new Map<string, (typeof local.estoque)[0]>();
  for (const e of [...(remoto.estoque || []), ...(local.estoque || [])]) {
    const prev = mapEst.get(e.id);
    if (!prev || (Date.parse(e.atualizadoEm) || 0) >= (Date.parse(prev.atualizadoEm) || 0)) {
      mapEst.set(e.id, e);
    }
  }

  const agendamentos = filtrarAgendamentosRemovidos([...mapAg.values()], remocoes);

  // Catálogo: o da loja (local) manda — evita a nuvem trocar serviços/barbeiros no update
  const servicos = local.servicos.length ? local.servicos : primary.servicos;
  const barbeiros = local.barbeiros.length ? local.barbeiros : primary.barbeiros;

  return {
    ...primary,
    config: { ...primary.config, ...local.config },
    servicos,
    barbeiros,
    agendamentos,
    remocoesAgendamento: remocoes,
    clientes: [...mapCli.values()],
    bloqueios: [...mapBlk.values()],
    despesas: mapDesp.size ? [...mapDesp.values()] : local.despesas,
    estoque: mapEst.size ? [...mapEst.values()] : local.estoque,
    atualizadoEm: preferLocal ? local.atualizadoEm : remoto.atualizadoEm,
  };
}
